'use client';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';

import { motion, AnimatePresence } from 'framer-motion';
import { useUser } from '@/hooks/use-user';
import { DashboardShell } from '@/layouts/DashboardShell';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { AtSign, Bell, UserPlus, Check, X, Loader2, CheckCheck, Inbox } from 'lucide-react';
import axiosInstance from '@/lib/axios';
import { toast } from 'sonner';
import type { Community } from '@/types';

interface NotificationItem {
  id: string;
  type: string;
  title: string;
  body: string;
  data: string | Record<string, unknown> | null;
  status: 'NEW' | 'READ';
  createdAt: string;
}

interface NotificationsData {
  notifications: NotificationItem[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  unreadCount: number;
}

const fetcher = (url: string) => axiosInstance.get(url).then((res) => res.data);

export default function NotificationsPage() {
  const { user, communities, isLoading: isUserLoading } = useUser();
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const {
    data: notifData,
    isLoading: isNotifLoading,
    mutate,
  } = useSWR<NotificationsData>(`/api/notifications?page=${page}&limit=20`, fetcher, {
    refreshInterval: 15000,
  });

  const notifications = notifData?.notifications || [];
  const pagination = notifData?.pagination;
  const unreadCount = notifData?.unreadCount || 0;

  const handleRespond = async (
    notificationId: string,
    friendRequestId: string,
    action: 'ACCEPT' | 'REJECT'
  ) => {
    try {
      // Optimistic update: mark the notification as READ
      mutate((current) => {
        if (!current) return current;
        return {
          ...current,
          notifications: current.notifications.map((n) =>
            n.id === notificationId ? { ...n, status: 'READ' as const } : n
          ),
          unreadCount: Math.max(0, current.unreadCount - 1),
        };
      }, false);

      toast.promise(
        axiosInstance.post(`/api/user/friends/request/${friendRequestId}/respond`, { action }),
        {
          loading: action === 'ACCEPT' ? 'Accepting friend request...' : 'Rejecting request...',
          success: () => {
            mutate();
            return action === 'ACCEPT' ? 'Friend request accepted!' : 'Friend request rejected.';
          },
          error: (err: unknown) => {
            mutate();
            const axiosErr = err as { response?: { data?: { error?: string } } };
            return axiosErr.response?.data?.error || 'Something went wrong';
          },
        }
      );
    } catch (e) {
      console.error(e);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      mutate((current) => {
        if (!current) return current;
        return {
          ...current,
          notifications: current.notifications.map((n) => ({ ...n, status: 'READ' as const })),
          unreadCount: 0,
        };
      }, false);

      await axiosInstance.patch('/api/notifications/read-all');
      mutate();
      toast.success('All notifications marked as read');
    } catch (e) {
      console.error(e);
      mutate();
      toast.error('Failed to mark all as read');
    }
  };

  const handleMarkRead = async (id: string) => {
    try {
      mutate((current) => {
        if (!current) return current;
        return {
          ...current,
          notifications: current.notifications.map((n) =>
            n.id === id ? { ...n, status: 'READ' as const } : n
          ),
          unreadCount: Math.max(0, current.unreadCount - 1),
        };
      }, false);

      await axiosInstance.patch(`/api/notifications/${id}/read`);
      mutate();
    } catch (e) {
      console.error(e);
      mutate();
    }
  };

  if (isUserLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary opacity-50" />
      </div>
    );
  }

  if (!user) {
    return <div className="p-8 text-center">Please login to view notifications</div>;
  }

  // Fallback if user is not in any communities yet
  const defaultCommunity = communities?.[0] || {
    id: 'milora',
    name: 'Milora',
  };

  return (
    <DashboardShell
      user={user}
      currentCommunity={defaultCommunity as Community}
      allCommunities={communities}
    >
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-3xl font-black tracking-tight text-foreground font-outfit">
              Notifications
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Stay updated on your connections and community activities.
            </p>
          </div>

          {unreadCount > 0 && (
            <Button
              onClick={handleMarkAllRead}
              variant="outline"
              size="sm"
              className="gap-2 rounded-xl font-bold bg-white/50 border-white/20 hover:bg-white transition-all shadow-sm shrink-0"
            >
              <CheckCheck className="h-4 w-4 text-primary" />
              Mark all as read
            </Button>
          )}
        </div>

        {isNotifLoading && notifications.length === 0 ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-10 w-10 animate-spin text-primary opacity-50" />
          </div>
        ) : notifications.length === 0 ? (
          <Card className="glass-card border-dashed border-2 border-border/50 rounded-3xl py-20 text-center">
            <CardContent className="space-y-4">
              <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto text-primary animate-bounce">
                <Inbox className="h-8 w-8" />
              </div>
              <div className="space-y-1">
                <h3 className="text-xl font-bold text-foreground">Inbox is empty</h3>
                <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                  You don&apos;t have any notifications right now. We&apos;ll let you know when
                  something new arrives!
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            <AnimatePresence initial={false}>
              {notifications.map((notif: NotificationItem) => {
                const isFriendReq = notif.type === 'FRIEND_REQUEST';
                const isNew = notif.status === 'NEW';

                let dataObj: {
                  senderId?: string;
                  senderImage?: string;
                  senderName?: string;
                  friendRequestId?: string;
                  friendId?: string;
                  friendName?: string;
                  eventId?: string;
                  eventTitle?: string;
                  communityId?: string;
                  userId?: string;
                  url?: string;
                  postId?: string;
                  leadId?: string;
                  thankYouId?: string;
                  testimonialId?: string;
                  meetupId?: string;
                  type?: string;
                } = {};

                if (notif.data) {
                  try {
                    dataObj = typeof notif.data === 'string' ? JSON.parse(notif.data) : notif.data;
                  } catch (e) {
                    console.error(e);
                  }
                }
                const friendRequestId = dataObj?.friendRequestId;
                const isMention =
                  dataObj?.type === 'MENTION' ||
                  dataObj?.type === 'MEMBER_MENTION' ||
                  dataObj?.type === 'BUSINESS_MENTION' ||
                  dataObj?.type === 'CATEGORY_MENTION';

                const targetUserId = dataObj?.senderId || dataObj?.friendId;

                const handleCardClick = () => {
                  if (isNew) {
                    handleMarkRead(notif.id);
                  }
                  if (
                    (notif.type === 'BIRTHDAY' || notif.type === 'ANNIVERSARY') &&
                    dataObj?.communityId
                  ) {
                    const params = new URLSearchParams({ view: 'social' });
                    if (dataObj.userId) {
                      params.set('userId', dataObj.userId);
                      params.set(
                        'celebration',
                        notif.type === 'BIRTHDAY' ? 'birthday' : 'anniversary'
                      );
                    }
                    navigate(`/${dataObj.communityId}/dashboard?${params.toString()}`);
                  } else if (dataObj?.url) {
                    navigate(dataObj.url);
                  } else if (
                    (notif.type === 'DIRECT_LEAD' ||
                      notif.type === 'THANK_YOU' ||
                      notif.type === 'TESTIMONIAL' ||
                      notif.type === 'MEETUP' ||
                      dataObj?.type === 'LEAD_INTEREST') &&
                    dataObj?.postId
                  ) {
                    if (dataObj.communityId) {
                      navigate(`/${dataObj.communityId}/posts/${dataObj.postId}`);
                    } else {
                      navigate(`/posts/${dataObj.postId}`);
                    }
                  } else if (targetUserId) {
                    const communityId = communities?.[0]?.id || 'milora';
                    navigate(`/${communityId}/u/${targetUserId}`);
                  }
                };

                return (
                  <motion.div
                    key={notif.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Card
                      className={`glass-card border-white/20 rounded-2xl overflow-hidden hover:shadow-md transition-all cursor-pointer ${
                        isNew
                          ? 'bg-primary/5 border-primary/20 ring-1 ring-primary/5'
                          : 'bg-white/40'
                      }`}
                      onClick={handleCardClick}
                    >
                      <CardContent className="p-4 flex items-start gap-4">
                        {dataObj?.senderImage ? (
                          <Avatar className="h-12 w-12 rounded-xl shadow-sm border border-white/40 shrink-0">
                            <AvatarImage src={dataObj.senderImage} className="object-cover" />
                            <AvatarFallback className="bg-primary/10 text-primary font-bold">
                              {dataObj.senderName?.[0] || 'U'}
                            </AvatarFallback>
                          </Avatar>
                        ) : (
                          <div
                            className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 shadow-sm ${
                              isNew
                                ? 'bg-primary/10 text-primary'
                                : 'bg-muted text-muted-foreground'
                            }`}
                          >
                            {isFriendReq ? (
                              <UserPlus className="h-5 w-5" />
                            ) : isMention ? (
                              <AtSign className="h-5 w-5" />
                            ) : (
                              <Bell className="h-5 w-5" />
                            )}
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h4 className="text-sm font-bold text-foreground truncate">
                              {notif.title}
                            </h4>
                            <div className="flex items-center gap-2 shrink-0">
                              {isNew && (
                                <Badge className="bg-blue-600 hover:bg-blue-600 text-white text-[9px] font-black uppercase tracking-wider py-0.5 px-1.5 rounded-full border-none">
                                  New
                                </Badge>
                              )}
                              <span className="text-[10px] text-muted-foreground">
                                {new Date(notif.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>

                          <p className="text-xs text-muted-foreground mt-1 leading-relaxed break-words">
                            {notif.body}
                          </p>

                          {isFriendReq && friendRequestId && isNew && (
                            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/20">
                              <Button
                                size="sm"
                                variant="default"
                                className="h-8 rounded-xl text-xs font-bold gap-1 px-4 shadow-sm bg-primary hover:bg-primary/90 text-white transition-all"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRespond(notif.id, friendRequestId, 'ACCEPT');
                                }}
                              >
                                <Check className="h-3.5 w-3.5" />
                                Accept
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 rounded-xl text-xs font-bold gap-1 px-4 border-gray-200 hover:bg-gray-50 text-gray-600 hover:text-gray-900 transition-all bg-white"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRespond(notif.id, friendRequestId, 'REJECT');
                                }}
                              >
                                <X className="h-3.5 w-3.5" />
                                Reject
                              </Button>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>

            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 pt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="rounded-xl"
                >
                  Previous
                </Button>
                <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest">
                  Page {page} of {pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={page === pagination.totalPages}
                  className="rounded-xl"
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
